import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, XCircle } from "lucide-react";
import { API, th, td, StatPill, money } from "./shared";
import { humanCategory } from "../Materials";
import { IncludeTestToggle } from "./TestAccounts";

const STATUSES = [["", "All"], ["live", "Live"], ["awarded", "Awarded"], ["cancelled", "Cancelled"]];
const tone = { live: "#1D4ED8", awarded: "#15803D", cancelled: "#B91C1C" };
const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Quotes({ rfqId }) {
  const [bids, setBids] = useState(null);
  useEffect(() => { axios.get(`${API}/material-rfqs/${rfqId}`).then(r => setBids(r.data.bids || [])).catch(() => setBids([])); }, [rfqId]);
  if (!bids) return <div style={{ fontSize: 12, color: "#64748b" }}>Loading quotes…</div>;
  if (!bids.length) return <div data-testid={`asr-no-quotes-${rfqId}`} style={{ fontSize: 12, color: "#64748b" }}>No quotes yet.</div>;
  return (
    <table data-testid={`asr-quotes-${rfqId}`} style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", background: "#fff" }}>
      <thead><tr style={{ textAlign: "left", background: "#f8fafc" }}><th style={th}>Supplier</th><th style={th}>Price</th><th style={th}>Turnaround</th><th style={th}>Note</th><th style={th}>Quoted</th></tr></thead>
      <tbody>{bids.map(b => (
        <tr key={b.id} data-testid={`asr-quote-${b.id}`} style={{ borderTop: "1px solid #e5e7eb" }}>
          <td style={td}>{b.supplier_name}</td><td style={{ ...td, fontWeight: 700 }}>{money(b.price)}</td>
          <td style={td}>{b.turnaround_days} days</td><td style={{ ...td, color: "#475569" }}>{b.note || "—"}</td><td style={td}>{fmtDate(b.updated_at || b.created_at)}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function CancelBox({ rfq, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await axios.post(`${API}/material-rfqs/${rfq.id}/cancel`, { reason }); toast.success("Sub-RFQ cancelled, printer and bidders notified"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); } finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
      <input data-testid={`asr-cancel-reason-${rfq.id}`} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason shown to the printer" style={{ flex: 1, padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 12.5 }}/>
      <button data-testid={`asr-cancel-confirm-${rfq.id}`} className="secondary-btn" disabled={busy || reason.trim().length < 5} onClick={go} style={{ color: "#B91C1C", fontSize: 12 }}>
        <XCircle size={13}/> {busy ? "Cancelling…" : "Cancel sub-RFQ"}
      </button>
    </div>
  );
}

function Row({ r, onDone }) {
  const [open, setOpen] = useState(false);
  const cancellable = r.status === "live" && !r.pending_order_id;
  return (
    <>
      <tr data-testid={`asr-row-${r.id}`} onClick={() => setOpen(!open)} style={{ borderBottom: "1px solid #e5e7eb", cursor: "pointer", background: open ? "#f8fafc" : undefined }}>
        <td style={td}><b>{(r.description || "").slice(0, 70)}</b><div style={{ fontSize: 10.5, color: "#9ca3af" }}>{r.id}</div></td>
        <td style={td}>{r.printer_name}</td>
        <td style={td}>{r.kind === "finishing" ? "Finishing" : "Material"} · {humanCategory(r.material_category)}</td>
        <td style={td}>{r.source === "own" ? `Own job (${r.own_job_reference || "—"})` : r.job_id}</td>
        <td style={td}>{r.quantity} {r.quantity_unit}</td>
        <td style={{ ...td, fontWeight: 700 }} data-testid={`asr-bidcount-${r.id}`}>{r.bid_count}</td>
        <td style={td}><span data-testid={`asr-status-${r.id}`} style={{ fontWeight: 700, color: tone[r.status] || "#334155", textTransform: "capitalize" }}>{r.status}</span>
          {r.cancelled_by === "admin" && <div style={{ fontSize: 10.5, color: "#B91C1C" }}>by admin</div>}</td>
        <td style={td}>{fmtDate(r.created_at)}</td>
      </tr>
      {open && (
        <tr data-testid={`asr-detail-${r.id}`}><td colSpan={8} style={{ ...td, background: "#f8fafc" }}>
          <div style={{ fontSize: 12.5, marginBottom: 8, color: "#334155" }}>{r.description}</div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 8 }}>Deadline {r.deadline_days} days · Ship to {r.ship_to_pincode}{r.expected_price_hint ? ` · Est. ${money(r.expected_price_hint)}` : ""}{r.awarded_supplier_name ? ` · Awarded to ${r.awarded_supplier_name}` : ""}{r.cancel_reason ? ` · Cancel reason: ${r.cancel_reason}` : ""}</div>
          <Quotes rfqId={r.id}/>
          {cancellable && <CancelBox rfq={r} onDone={onDone}/>}
        </td></tr>
      )}
    </>
  );
}

export function AdminSubRfqs() {
  const [status, setStatus] = useState("");
  const [incTest, setIncTest] = useState(false);
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${API}/material-rfqs`, { params: { include_test: incTest, ...(status ? { status } : {}) } }).then(r => setRows(r.data.rfqs || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [status, incTest]); // eslint-disable-line react-hooks/exhaustive-deps
  const all = rows || [];
  return (
    <section className="content" data-testid="admin-sub-rfqs-page">
      <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}><Package size={20}/> Sub-RFQs</h2>
      <p style={{ color: "#64748b", fontSize: 13, marginTop: 0 }}>Material and finishing requests posted by printers. They go live instantly; you can watch quotes and cancel any that look wrong.</p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "14px 0" }}>
        <StatPill label="Shown" value={all.length} testid="asr-stat-total"/>
        <StatPill label="Live" value={all.filter(r => r.status === "live").length} testid="asr-stat-live"/>
        <StatPill label="Quotes" value={all.reduce((s, r) => s + (r.bid_count || 0), 0)} testid="asr-stat-quotes"/>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <IncludeTestToggle value={incTest} onChange={setIncTest} testid="asr-include-test"/>
        {STATUSES.map(([v, l]) => (
          <button key={v || "all"} data-testid={`asr-filter-${v || "all"}`} className={status === v ? "primary-btn" : "secondary-btn"} style={{ fontSize: 12, padding: "5px 12px" }} onClick={() => setStatus(v)}>{l}</button>
        ))}
      </div>
      {rows === null ? <div>Loading…</div> : !all.length ? <div data-testid="asr-empty" style={{ color: "#64748b", padding: 20 }}>No sub-RFQs found.</div> : (
        <div style={{ overflowX: "auto" }}>
          <table data-testid="asr-table" style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
            <thead><tr style={{ background: "#f3f4f6", textAlign: "left" }}>
              <th style={th}>Request</th><th style={th}>Printer</th><th style={th}>Type</th><th style={th}>For job</th><th style={th}>Qty</th><th style={th}>Quotes</th><th style={th}>Status</th><th style={th}>Posted</th>
            </tr></thead>
            <tbody>{all.map(r => <Row key={r.id} r={r} onDone={load}/>)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
